import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';

export type ErrorBoundaryProps = { children: ReactNode };
type ErrorBoundaryState = { error: Error | null };

/**
 * Keeps a render-time throw from blanking the console.
 *
 * A class is required: React exposes no hook equivalent of
 * `componentDidCatch`. Reload rather than reset, because a component that
 * threw once will usually throw again on the same state.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // No telemetry endpoint exists, and none should: this is an on-premises
    // deployment that must make no outbound request. The console is the log.
    console.error('Unhandled render error', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <main className="mx-auto flex max-w-[42rem] flex-col gap-(--space-4) px-(--space-5) py-(--space-8)">
        <h1 className="text-[length:var(--text-title)]">Something broke in the console</h1>
        <Callout tone="danger">{error.message}</Callout>
        <p className="text-ink-muted">
          Nothing you were viewing has been changed. Reloading usually clears it; if it does not,
          the message above is what an operator needs.
        </p>
        <div>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Reload the console
          </Button>
        </div>
      </main>
    );
  }
}
